import { motion, AnimatePresence } from "framer-motion";
import { Mail, CheckCircle2, Loader2, X } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";

interface SubscribeConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  busy: boolean;
  email: string | null;
  viewMode?: string;
}

export function SubscribeConfirmModal({
  open,
  onOpenChange,
  onConfirm,
  busy,
  email,
  viewMode,
}: SubscribeConfirmModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
              />
            </Dialog.Overlay>
            <Dialog.Content asChild>
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.2, type: "spring", bounce: 0.3 }}
                className="fixed left-[50%] top-[50%] z-50 flex w-[90vw] max-w-[400px] translate-x-[-50%] translate-y-[-50%] flex-col rounded-xl border border-white/10 bg-zinc-950 p-6 shadow-2xl outline-none"
              >
                <div className="flex flex-col gap-4">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-full ${viewMode === "signal" ? "bg-emerald-500/10 text-emerald-400" : "bg-sky-500/10 text-sky-400"}`}>
                    <Mail className="h-6 w-6" strokeWidth={2} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Dialog.Title className="text-xl font-semibold tracking-tight text-white">
                      Subscribe to this Alert
                    </Dialog.Title>
                    <Dialog.Description className="text-[14px] leading-relaxed text-zinc-400">
                      This is <strong className="text-white">completely free of charge</strong> for subscribed users. Please confirm that you want to receive emails at <strong className="text-white">{email ?? "your email"}</strong> when this signal fires or surfaces something.
                    </Dialog.Description>
                  </div>
                </div>

                <div className="mt-8 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    disabled={busy}
                    className="inline-flex h-10 items-center justify-center rounded-lg px-4 text-[13px] font-semibold text-zinc-300 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={busy}
                    className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg px-5 text-[13px] font-semibold text-white shadow-sm transition-colors disabled:opacity-50 ${viewMode === "signal" ? "bg-emerald-500 hover:bg-emerald-400" : "bg-sky-500 hover:bg-sky-400"}`}
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" strokeWidth={2.4} />
                    )}
                    Confirm Subscription
                  </button>
                </div>

                <Dialog.Close asChild>
                  <button
                    type="button"
                    className={`absolute right-4 top-4 rounded-md p-1 text-zinc-500 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 disabled:pointer-events-none ${viewMode === "signal" ? "focus:ring-emerald-500" : "focus:ring-sky-500"}`}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </button>
                </Dialog.Close>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}